import { Injectable } from '@angular/core';
import { ApiBaseService } from './api-base.service';

/** Contact types the server-side search knows. */
export type ContactSearchType = 'person' | 'company' | 'society';

/** One hit of ContactsService/SearchContacts, best hit first. */
export interface ContactSearchItem {
  id: number;
  contactType: ContactSearchType;
  /** With title: "Dr. Katharina Becker". */
  displayName: string;
  address: string | null;
  zipcode: string | null;
  city: string | null;
  email: string | null;
  projectCount: number;
  tags: number[];
  /** How the weakest search word matched: exact, prefix, phonetic or fuzzy. */
  match: 'exact' | 'prefix' | 'phonetic' | 'fuzzy';
}

/**
 * ok: the hits are in items (possibly none).
 * tooShort: fewer than CONTACT_SEARCH_MIN_LENGTH characters.
 * needsName: nothing left to search for once titles are removed ("Dr.").
 * error: the request failed.
 */
export type ContactSearchStatus = 'ok' | 'tooShort' | 'needsName' | 'error';

export interface ContactSearchResult {
  status: ContactSearchStatus;
  items: ContactSearchItem[];
}

export const CONTACT_SEARCH_MIN_LENGTH = 2;
export const CONTACT_SEARCH_DEBOUNCE_MS = 300;

/** Error-tolerant contact search on the server (Kölner Phonetik, typos, umlauts, titles). */
@Injectable({
  providedIn: 'root',
})
export class ContactSearchService {
  constructor(private apiService: ApiBaseService) {}

  async search(query: string, types?: ContactSearchType[], take?: number): Promise<ContactSearchResult> {
    // No global loader: the search runs while the user types.
    const result = await this.apiService.httpRequest(
      'ContactsService/SearchContacts',
      { Query: query, Types: types ?? [], Take: take },
      true,
      false
    );
    if (!result || result.hasError) {
      return { status: 'error', items: [] };
    }
    return { status: result.status ?? 'ok', items: result.items ?? [] };
  }

  /** One search field: waits for a typing pause, skips short input and drops answers to older queries. */
  createSession(
    types: ContactSearchType[],
    onChange: (session: ContactSearchSession) => void,
    take?: number
  ): ContactSearchSession {
    return new ContactSearchSession(this, types, onChange, take);
  }
}

/**
 * State of one search field. While `active`, a list shows only the hits (`isHit`) in the
 * server's order (`rankOf`) instead of filtering by itself; below the minimum length it
 * shows its full list as before.
 */
export class ContactSearchSession {
  /** The query the current state belongs to (trimmed). */
  query = '';
  /** True once a query of at least the minimum length has an answer. */
  active = false;
  /** A request for the current input is pending (typing pause or server). */
  pending = false;
  status: ContactSearchStatus = 'tooShort';
  items: ContactSearchItem[] = [];

  private ranks = new Map<string, number>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private sequence = 0;

  constructor(
    private service: ContactSearchService,
    private types: ContactSearchType[],
    private onChange: (session: ContactSearchSession) => void,
    private take?: number
  ) {}

  setQuery(text: string | null | undefined): void {
    const query = (text ?? '').trim();
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;

    if (query.length < CONTACT_SEARCH_MIN_LENGTH) {
      this.sequence++; // an answer still on its way is outdated
      const changed = this.active || this.pending;
      this.reset(query);
      if (changed) this.onChange(this);
      return;
    }
    if (query === this.query && this.active && !this.pending) return;

    this.sequence++; // an answer still on its way belongs to the previous input
    this.pending = true;
    this.timer = setTimeout(() => this.run(query), CONTACT_SEARCH_DEBOUNCE_MS);
  }

  /** Whether the contact is one of the hits (only meaningful while active). */
  isHit(type: ContactSearchType, id: number): boolean {
    return this.ranks.has(`${type}:${id}`);
  }

  /** Position in the server's ranking; contacts that are no hit come last. */
  rankOf(type: ContactSearchType, id: number): number {
    return this.ranks.get(`${type}:${id}`) ?? Number.MAX_SAFE_INTEGER;
  }

  hasHits(type: ContactSearchType): boolean {
    return this.items.some(i => i.contactType === type);
  }

  dispose(): void {
    if (this.timer) clearTimeout(this.timer);
    this.sequence++;
  }

  private async run(query: string): Promise<void> {
    this.timer = null;
    const mine = ++this.sequence;
    let result: ContactSearchResult;
    try {
      result = await this.service.search(query, this.types, this.take);
    } catch {
      result = { status: 'error', items: [] };
    }
    if (mine !== this.sequence) return;

    this.query = query;
    this.active = true;
    this.pending = false;
    this.status = result.status;
    this.items = result.items;
    this.ranks = new Map(result.items.map((item, index) => [`${item.contactType}:${item.id}`, index]));
    this.onChange(this);
  }

  private reset(query: string): void {
    this.query = query;
    this.active = false;
    this.pending = false;
    this.status = 'tooShort';
    this.items = [];
    this.ranks = new Map();
  }
}

/** A piece of a name; `hit` marks the part that matches what was typed. */
export interface HighlightSegment {
  text: string;
  hit: boolean;
}

const SPELLED_OUT: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };

/**
 * Lowercase, umlauts spelled out and accents dropped, as the server compares names; `source`
 * maps every folded character back to its position in the original text.
 */
function foldWithMap(text: string): { folded: string; source: number[] } {
  let folded = '';
  const source: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const lower = text[i].toLowerCase();
    const spelled = SPELLED_OUT[lower] ?? lower.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    for (const c of spelled) {
      folded += c;
      source.push(i);
    }
  }
  return { folded, source };
}

/**
 * Splits a name into plain and highlighted parts: every word that starts with a typed word is
 * highlighted for the typed length ("Mai" in "Maier"; "Mueller" also in "Müller"). Sound-alike
 * and typo hits ("Mayer" for "Maier") have no literal match and stay plain.
 */
export function highlightSegments(text: string | null | undefined, query: string | null | undefined): HighlightSegment[] {
  const value = text ?? '';
  const tokens = foldWithMap(query ?? '').folded.split(/[^\p{L}\p{N}]+/u).filter(t => t.length > 0);
  if (!value || tokens.length === 0) return [{ text: value, hit: false }];

  const { folded, source } = foldWithMap(value);
  const marked = new Array<boolean>(value.length).fill(false);
  const isWordStart = (i: number) => i === 0 || !/[\p{L}\p{N}]/u.test(folded[i - 1]);
  for (const token of tokens) {
    let at = folded.indexOf(token);
    while (at !== -1) {
      if (isWordStart(at)) {
        for (let k = source[at]; k <= source[at + token.length - 1]; k++) marked[k] = true;
      }
      at = folded.indexOf(token, at + 1);
    }
  }

  const segments: HighlightSegment[] = [];
  for (let i = 0; i < value.length; i++) {
    const last = segments[segments.length - 1];
    if (last && last.hit === marked[i]) last.text += value[i];
    else segments.push({ text: value[i], hit: marked[i] });
  }
  return segments;
}

/** Shown while a search is on its way (typing pause and server). */
export function contactSearchLoadingText(): string {
  return $localize`:@@contactSearchLoading:Searching …`;
}

/** "Hauptstr. 1, 54290 Trier": the grey second line under a contact's name. */
export function contactAddressLine(contact: { address?: string | null; zipcode?: string | null; city?: string | null }): string {
  const place = [contact?.zipcode, contact?.city].filter(p => !!p && p.trim()).join(' ');
  return [contact?.address, place].filter(p => !!p && p.trim()).join(', ');
}

/**
 * Empty-state / hint text for a search that found nothing, or null while there is nothing to say.
 * With `type`, for one of several lists sharing the search: while another list has hits, this one
 * just says it has none (the spelling is evidently fine).
 */
export function contactSearchMessage(session: ContactSearchSession | null | undefined, type?: ContactSearchType): string | null {
  if (!session?.active) return null;
  if (type && session.status === 'ok' && session.items.length > 0 && !session.hasHits(type)) {
    return $localize`:@@contactSearchNoHitsHere:No results`;
  }
  switch (session.status) {
    case 'needsName':
      return $localize`:@@contactSearchNeedsName:Please also enter a name.`;
    case 'error':
      return $localize`:@@contactSearchFailed:Search is not available right now. Please try again.`;
    default:
      return $localize`:@@contactSearchNoHits:No results for “${session.query}:query:”. Check the spelling.`;
  }
}
